import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

const CreateRidePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { rideId } = useParams<{ rideId?: string }>();
  const isEditMode = !!rideId;
  
  const [loading, setLoading] = useState(false);
  const [loadingRide, setLoadingRide] = useState(isEditMode);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    departureLocation: '',
    departureDate: '',
    departureTime: '',
    distanceKm: '',
    maxRiders: '',
    elevationGainM: '',
    estimatedDurationMinutes: '',
    cost: '',
    visibility: 'PUBLIC' as 'PUBLIC' | 'MEMBERS_ONLY' | 'INVITE_ONLY',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Premium feature checks
  const [hasClubPremium, setHasClubPremium] = useState(false);
  const [hasStoreProfessional, setHasStoreProfessional] = useState(false);
  const [clubId, setClubId] = useState<string | null>(null);
  const [clubMembers, setClubMembers] = useState<Array<{ id: string; userId: string; user: { firstName: string; lastName: string; email: string } }>>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('rides.createRideTitleRequired');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('rides.createRideDescriptionRequired');
    }

    if (!formData.departureLocation.trim()) {
      newErrors.departureLocation = t('rides.createRideLocationRequired');
    }

    if (!formData.departureDate.trim()) {
      newErrors.departureDate = t('rides.createRideDateRequired');
    } else {
      const date = new Date(formData.departureDate);
      if (isNaN(date.getTime())) {
        newErrors.departureDate = t('rides.createRideDateInvalid');
      }
    }

    if (!formData.departureTime.trim()) {
      newErrors.departureTime = t('rides.createRideTimeRequired');
    } else {
      const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(formData.departureTime)) {
        newErrors.departureTime = t('rides.createRideTimeInvalid');
      }
    }

    if (!formData.distanceKm || isNaN(Number(formData.distanceKm)) || Number(formData.distanceKm) <= 0) {
      newErrors.distanceKm = t('rides.createRideDistanceRequired');
    }

    if (!formData.maxRiders || isNaN(Number(formData.maxRiders)) || Number(formData.maxRiders) <= 0) {
      newErrors.maxRiders = t('rides.createRideMaxRidersRequired');
    }

    if (formData.elevationGainM && (isNaN(Number(formData.elevationGainM)) || Number(formData.elevationGainM) < 0)) {
      newErrors.elevationGainM = t('rides.createRideElevationInvalid');
    }

    if (formData.estimatedDurationMinutes && (isNaN(Number(formData.estimatedDurationMinutes)) || Number(formData.estimatedDurationMinutes) <= 0)) {
      newErrors.estimatedDurationMinutes = t('rides.createRideDurationInvalid');
    }

    if (formData.cost && (isNaN(Number(formData.cost)) || Number(formData.cost) < 0)) {
      newErrors.cost = t('rides.createRideCostInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('rides.createRideValidationError'));
      return;
    }

    setLoading(true);

    try {
      const rideData: any = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        departureLocation: formData.departureLocation.trim(),
        departureDate: formData.departureDate,
        departureTime: formData.departureTime,
        distanceKm: Number(formData.distanceKm),
        maxRiders: Number(formData.maxRiders),
      };

      // Add optional fields if provided
      if (formData.elevationGainM) {
        rideData.elevationGainM = Number(formData.elevationGainM);
      }
      if (formData.estimatedDurationMinutes) {
        rideData.estimatedDurationMinutes = Number(formData.estimatedDurationMinutes);
      }
      if (formData.cost) {
        rideData.cost = Number(formData.cost);
      }

      let rideIdToUse: string;
      
      if (isEditMode && rideId) {
        // Update existing ride
        const formDataToSend = new FormData();
        Object.keys(rideData).forEach(key => {
          formDataToSend.append(key, rideData[key]);
        });
        
        await api.put(`/admin/rides/${rideId}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        rideIdToUse = rideId;
      } else {
        // Create new ride
        const response = await api.post('/admin/rides', rideData);
        rideIdToUse = response.data.ride?.id;
      }
      
      // Update visibility separately (for both create and update)
      if (rideIdToUse && formData.visibility && formData.visibility !== 'PUBLIC') {
        try {
          const visibilityPayload: any = {
            visibility: formData.visibility
          };
          
          // For INVITE_ONLY rides with club, include invited member IDs
          if (formData.visibility === 'INVITE_ONLY' && clubId && selectedMemberIds.length > 0) {
            visibilityPayload.invitedMemberIds = selectedMemberIds;
          }
          
          await api.put(`/api/rides/${rideIdToUse}/visibility`, visibilityPayload);
        } catch (error: any) {
          console.error('Failed to set ride visibility:', error);
          toast.error(`Ride ${isEditMode ? 'updated' : 'created'} but visibility setting failed: ` + (error.response?.data?.error || error.message));
        }
      }
      
      toast.success(isEditMode ? t('rides.updateRideSuccess') || 'Ride updated successfully!' : t('rides.createRideSuccess'));
      navigate(`/rides/${rideIdToUse}`);
    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} ride:`, error);
      const errorMessage = error.response?.data?.error || error.message || (isEditMode ? t('rides.updateRideError') || 'Failed to update ride' : t('rides.createRideError'));
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch existing ride data if in edit mode
  useEffect(() => {
    const fetchRideData = async () => {
      if (!rideId || !user) return;
      
      setLoadingRide(true);
      try {
        // Fetch ride from admin endpoint - now uses optimized single-ride endpoint
        const rideResponse = await api.get(`/admin/rides/${rideId}`);
        const ride = rideResponse.data.ride;
        
        if (!ride) {
          toast.error('Ride not found');
          navigate('/rides');
          return;
        }
        
        // Format date for form (YYYY-MM-DD)
        const dateStr = ride.departureDate 
          ? new Date(ride.departureDate).toISOString().split('T')[0] 
          : '';
        
        // Pre-fill form with existing ride data
        setFormData({
          name: ride.name || '',
          description: ride.description || '',
          departureLocation: ride.departureLocation || '',
          departureDate: dateStr,
          departureTime: ride.departureTime || '',
          distanceKm: ride.distanceKm?.toString() || '',
          maxRiders: ride.maxRiders?.toString() || '',
          elevationGainM: ride.elevationGainM?.toString() || '',
          estimatedDurationMinutes: ride.estimatedDurationMinutes?.toString() || '',
          cost: ride.cost?.toString() || '',
          visibility: ride.visibility || 'PUBLIC',
        });
        
        // Set clubId if it's a club ride
        if (ride.clubId) {
          setClubId(ride.clubId);
        }
        
        // If INVITE_ONLY, get invited members from ride data
        if (ride.visibility === 'INVITE_ONLY' && ride.clubId && ride.invitedClubMembers) {
          try {
            const invitedIds = ride.invitedClubMembers.map((m: any) => m.id);
            setSelectedMemberIds(invitedIds);
          } catch (error) {
            console.error('Error processing invited members:', error);
          }
        }
      } catch (error: any) {
        console.error('Error fetching ride data:', error);
        toast.error('Failed to load ride data');
        navigate('/rides');
      } finally {
        setLoadingRide(false);
      }
    };
    
    if (isEditMode) {
      fetchRideData();
    }
  }, [rideId, user, isEditMode, navigate]);

  // Check premium features on mount
  useEffect(() => {
    const checkPremiumFeatures = async () => {
      if (!user) return;
      
      if (user.role === 'CLUB_ADMIN' && user.clubId) {
        try {
          setClubId(user.clubId);
          const response = await api.get(`/api/clubs/${user.clubId}`);
          const club = response.data.club;
          
          // Check if club has premium (subscription or trial)
          const activeSubscription = club.subscriptions?.[0];
          const hasPremiumSubscription = activeSubscription?.planId === 'CLUB_PREMIUM';
          const hasActiveTrial = club.trialEndsAt && new Date(club.trialEndsAt) >= new Date();
          const hasPremium = hasPremiumSubscription || hasActiveTrial;
          
          setHasClubPremium(hasPremium);
          
          // Fetch club members if premium (for invitation UI)
          if (hasPremium) {
            await fetchClubMembers(user.clubId);
          }
        } catch (error) {
          console.error('Error fetching club info:', error);
        }
      } else if ((user.role === 'STORE_ADMIN' || user.role === 'STORE_MANAGER') && user.storeId) {
        try {
          const response = await api.get(`/api/stores/${user.storeId}`);
          const store = response.data.store;
          const hasProfessional = store.subscription?.planId === 'PROFESSIONAL';
          setHasStoreProfessional(hasProfessional);
        } catch (error) {
          console.error('Error fetching store info:', error);
        }
      }
    };
    
    checkPremiumFeatures();
  }, [user]);

  // Fetch club members for invitation UI
  const fetchClubMembers = async (clubId: string) => {
    setLoadingMembers(true);
    try {
      const response = await api.get(`/api/clubs/${clubId}/members`);
      const members = response.data.members || [];
      // Filter to only active members and map to expected structure
      const activeMembers = members
        .filter((m: any) => m.status === 'ACTIVE')
        .map((m: any) => ({
          id: m.id,
          userId: m.userId,
          user: {
            firstName: m.user.firstName,
            lastName: m.user.lastName,
            email: m.user.email
          }
        }));
      setClubMembers(activeMembers);
    } catch (error) {
      console.error('Error fetching club members:', error);
      toast.error('Failed to load club members');
    } finally {
      setLoadingMembers(false);
    }
  };

  // Check if user has permission to create rides
  const canCreateRide = user?.role === 'STORE_MANAGER' || 
                       user?.role === 'STORE_ADMIN' || 
                       user?.role === 'CLUB_ADMIN' ||
                       user?.role === 'COMMUNITY_MANAGER' ||
                       user?.role === 'ADMIN' ||
                       user?.role === 'SYSTEM_ADMIN' ||
                       user?.role === 'SUPERADMIN';

  if (!canCreateRide) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('rides.createRidePermissionDenied')}</h2>
          <p className="text-gray-600 mb-6">{t('rides.createRidePermissionMessage')}</p>
          <button
            onClick={() => navigate('/rides')}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  if (loadingRide) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ride data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/rides')}
          className="text-primary-600 hover:text-primary-700 mb-4"
        >
          ← {t('common.back')}
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? (t('rides.editRide') || 'Edit Ride') : t('rides.createRide')}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Ride Title */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              {t('rides.createRideTitle')} *
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              value={formData.name}
              onChange={handleChange}
              placeholder={t('rides.createRideTitlePlaceholder')}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              {t('rides.createRideDescription')} *
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows={4}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
              value={formData.description}
              onChange={handleChange}
              placeholder={t('rides.createRideDescriptionPlaceholder')}
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          {/* Departure Location */}
          <div>
            <label htmlFor="departureLocation" className="block text-sm font-medium text-gray-700 mb-2">
              {t('rides.startLocation')} *
            </label>
            <input
              id="departureLocation"
              name="departureLocation"
              type="text"
              required
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                errors.departureLocation ? 'border-red-300' : 'border-gray-300'
              }`}
              value={formData.departureLocation}
              onChange={handleChange}
              placeholder={t('rides.createRideLocationPlaceholder')}
            />
            {errors.departureLocation && <p className="mt-1 text-sm text-red-600">{errors.departureLocation}</p>}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="departureDate" className="block text-sm font-medium text-gray-700 mb-2">
                {t('rides.createRideDate')} *
              </label>
              <input
                id="departureDate"
                name="departureDate"
                type="date"
                required
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                  errors.departureDate ? 'border-red-300' : 'border-gray-300'
                }`}
                value={formData.departureDate}
                onChange={handleChange}
              />
              {errors.departureDate && <p className="mt-1 text-sm text-red-600">{errors.departureDate}</p>}
            </div>

            <div>
              <label htmlFor="departureTime" className="block text-sm font-medium text-gray-700 mb-2">
                {t('rides.createRideTime')} *
              </label>
              <input
                id="departureTime"
                name="departureTime"
                type="time"
                required
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                  errors.departureTime ? 'border-red-300' : 'border-gray-300'
                }`}
                value={formData.departureTime}
                onChange={handleChange}
              />
              {errors.departureTime && <p className="mt-1 text-sm text-red-600">{errors.departureTime}</p>}
            </div>
          </div>

          {/* Distance and Max Riders */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="distanceKm" className="block text-sm font-medium text-gray-700 mb-2">
                {t('rides.distance')} (km) *
              </label>
              <input
                id="distanceKm"
                name="distanceKm"
                type="number"
                min="0"
                step="0.1"
                required
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                  errors.distanceKm ? 'border-red-300' : 'border-gray-300'
                }`}
                value={formData.distanceKm}
                onChange={handleChange}
                placeholder="0"
              />
              {errors.distanceKm && <p className="mt-1 text-sm text-red-600">{errors.distanceKm}</p>}
            </div>

            <div>
              <label htmlFor="maxRiders" className="block text-sm font-medium text-gray-700 mb-2">
                {t('rides.maxParticipants')} *
              </label>
              <input
                id="maxRiders"
                name="maxRiders"
                type="number"
                min="1"
                required
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                  errors.maxRiders ? 'border-red-300' : 'border-gray-300'
                }`}
                value={formData.maxRiders}
                onChange={handleChange}
                placeholder="0"
              />
              {errors.maxRiders && <p className="mt-1 text-sm text-red-600">{errors.maxRiders}</p>}
            </div>
          </div>

          {/* Optional Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="elevationGainM" className="block text-sm font-medium text-gray-700 mb-2">
                {t('rides.createRideElevation')} (m)
              </label>
              <input
                id="elevationGainM"
                name="elevationGainM"
                type="number"
                min="0"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                  errors.elevationGainM ? 'border-red-300' : 'border-gray-300'
                }`}
                value={formData.elevationGainM}
                onChange={handleChange}
                placeholder="0"
              />
              {errors.elevationGainM && <p className="mt-1 text-sm text-red-600">{errors.elevationGainM}</p>}
            </div>

            <div>
              <label htmlFor="estimatedDurationMinutes" className="block text-sm font-medium text-gray-700 mb-2">
                {t('rides.createRideDuration')} (min)
              </label>
              <input
                id="estimatedDurationMinutes"
                name="estimatedDurationMinutes"
                type="number"
                min="1"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                  errors.estimatedDurationMinutes ? 'border-red-300' : 'border-gray-300'
                }`}
                value={formData.estimatedDurationMinutes}
                onChange={handleChange}
                placeholder="0"
              />
              {errors.estimatedDurationMinutes && <p className="mt-1 text-sm text-red-600">{errors.estimatedDurationMinutes}</p>}
            </div>

            <div>
              <label htmlFor="cost" className="block text-sm font-medium text-gray-700 mb-2">
                {t('rides.createRideCost')}
              </label>
              <input
                id="cost"
                name="cost"
                type="number"
                min="0"
                step="0.01"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                  errors.cost ? 'border-red-300' : 'border-gray-300'
                }`}
                value={formData.cost}
                onChange={handleChange}
                placeholder="0.00"
              />
              {errors.cost && <p className="mt-1 text-sm text-red-600">{errors.cost}</p>}
            </div>
          </div>

          {/* Ride Visibility Controls - Only for clubs with premium or stores with professional */}
          {(hasClubPremium || hasStoreProfessional) && (
            <div className="border-t pt-6">
              <div className="mb-4">
                <label htmlFor="visibility" className="block text-sm font-medium text-gray-700 mb-2">
                  Ride Visibility *
                </label>
                <select
                  id="visibility"
                  name="visibility"
                  required
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                    errors.visibility ? 'border-red-300' : 'border-gray-300'
                  }`}
                  value={formData.visibility}
                  onChange={(e) => {
                    const newVisibility = e.target.value as 'PUBLIC' | 'MEMBERS_ONLY' | 'INVITE_ONLY';
                    setFormData({ ...formData, visibility: newVisibility });
                    // Clear selected members if switching away from INVITE_ONLY
                    if (newVisibility !== 'INVITE_ONLY') {
                      setSelectedMemberIds([]);
                    }
                  }}
                >
                  <option value="PUBLIC">Public - Visible to everyone</option>
                  <option value="MEMBERS_ONLY">Members Only - Only club/store members can see</option>
                  <option value="INVITE_ONLY">Invite Only - Only invited members can see</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  {formData.visibility === 'PUBLIC' && 'This ride will be visible to all users'}
                  {formData.visibility === 'MEMBERS_ONLY' && 'Only active club/store members can see and join this ride'}
                  {formData.visibility === 'INVITE_ONLY' && 'Only members you invite can see and join this ride'}
                </p>
                {errors.visibility && <p className="mt-1 text-sm text-red-600">{errors.visibility}</p>}
              </div>

              {/* Member Invitation UI for INVITE_ONLY rides (clubs only) */}
              {formData.visibility === 'INVITE_ONLY' && clubId && hasClubPremium && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invite Members (Optional)
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Select members to invite to this ride. Only invited members will be able to see and join.
                  </p>
                  {loadingMembers ? (
                    <div className="text-sm text-gray-500">Loading members...</div>
                  ) : clubMembers.length === 0 ? (
                    <div className="text-sm text-gray-500">No active members found</div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded bg-white">
                      {clubMembers.map((member) => (
                        <label
                          key={member.id}
                          className="flex items-center p-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedMemberIds.includes(member.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMemberIds([...selectedMemberIds, member.id]);
                              } else {
                                setSelectedMemberIds(selectedMemberIds.filter(id => id !== member.id));
                              }
                            }}
                            className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {member.user.firstName} {member.user.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{member.user.email}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  {selectedMemberIds.length > 0 && (
                    <p className="mt-2 text-sm text-gray-600">
                      {selectedMemberIds.length} member{selectedMemberIds.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/rides')}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading 
              ? (isEditMode ? (t('rides.updateRideUpdating') || 'Updating...') : t('rides.createRideCreating'))
              : (isEditMode ? (t('rides.updateRideButton') || 'Update Ride') : t('rides.createRideButton'))
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateRidePage;
